import { add } from 'date-fns';
import { isEmpty } from 'lodash-es';
import React, { memo } from 'react';
import PostType, { CampaignType } from '@/constants/postType.constants';
import useStore from '@/stores/store';
import getQualifiedImageUrl from '@/utils/imageUrlProcessor';
import { Controller, useFormContext } from 'react-hook-form';
import PostViewersSelector from '@/components/PostViewerSelector/PostViewersSelector';
import PostViewersSelectorNekiAdmin from '@/components/PostViewerSelector/PostViewersSelectorNekiAdmin';
import Typography from '@/components/next-ui/Typography';
import { useIsCompanyAdmin, useIsNonProfitAdmin } from '@/hooks/useUser';
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Input from '@ui/Input';
import TextArea from '@ui/TextArea';
import ImageCarousel from '../ImageCarousel';
import NonProfitSearchBox from './NonProfitSearchBox';
import ProjectSelector from './ProjectSelector';
import OrgType from '@/constants/orgType.constant';
import PostNotify from '../PostNotify';

function CreateCampaignForm(props) {
  const {
    isEditMode,
    type,
    images,
    videos,
    isNekiAdmin,
    fundRaiseType = 'None',
    isP2PFundraiser = false,
  } = props;
  const userProfile = useStore((state) => state.user.profile);
  const isNonProfitAdmin = useIsNonProfitAdmin();
  const isCompanyAdmin = useIsCompanyAdmin();
  const [projectImages, setProjectImages] = React.useState([]);
  const [projectVideos, setProjectVideos] = React.useState([]);

  const quantityPrefix =
    type === CampaignType.FundRaise ? userProfile.location.currencySymbol : '';
  const descriptionTooltip =
    type === CampaignType.FundRaise
      ? 'What are you fundraising for? Tell a story that makes your campaign personal.'
      : 'What are you collecting goods for? Tell a story that makes your campaign personal.';

  const {
    formState: { errors },
    control,
    getValues,
    setValue,
    watch,
  } = useFormContext();

  const [selectedNonProfit, selectedProject] = watch(['nonProfit', 'project']);

  setValue('fundRaiseType', fundRaiseType);

  const projects =
    selectedNonProfit?.projects?.filter((project) => {
      return project.campaignType === type;
    }) || [];

  const clearFormOnNonProfitChange = () => {
    if (!isEditMode) {
      setProjectImages([]);
      setProjectVideos([]);
      setValue('project', null);
      setValue('title', '');
      setValue('description', '');
      setValue('address', '');
      setValue('goalAmount', null);
      setValue('startDate', new Date());
      setValue('endDate', add(new Date(), { days: 90 }));
    }
  };

  const handleProjectChange = (project) => {
    if (!isEditMode) {
      if (project) {
        setProjectImages(
          project.imageUrl?.map((image) => {
            return {
              preview: getQualifiedImageUrl(image),
            };
          }) || [],
        );
        setProjectVideos(
          project.videoUrl?.map((video) => {
            return {
              url: video,
            };
          }) || [],
        );

        const projectToCampaignFields = [
          { project: 'name', campaign: 'title' },
          { project: 'description', campaign: 'description' },
          { project: 'address', campaign: 'address' },
        ];

        projectToCampaignFields.forEach((field) => {
          setValue(field.campaign, project[field.project], {
            shouldValidate: true,
            shouldDirty: true,
          });
        });
      } else {
        setProjectImages([]);
        setProjectVideos([]);
      }
    }
  };

  return (
    <div className="flex w-full flex-col gap-6 pb-4 pt-6">
      <div className="mb-1.5 ml-1.5">
        {/* {isNekiAdmin ? (
          <PostViewersSelectorNekiAdmin
            disabled={isEditMode}
            isP2PFundraiser={isP2PFundraiser}
          />
        ) : (
          <PostViewersSelector
            disabled={isEditMode}
            isP2PFundraiser={isP2PFundraiser}
          />
        )} */}

        <PostViewersSelector
          disabled={isEditMode}
          isP2PFundraiser={isP2PFundraiser}
        />
      </div>

      {/* <div>
        {isCompanyAdmin ? (
          <PostNotify
            postType={PostType.UserPost}
            orgId={userProfile?.activeCommunity.id}
            orgType={userProfile?.activeCommunity?.orgType}
          />
        ) : (
          <PostNotify
            postType={PostType.Campaign}
            orgId={
              isNonProfitAdmin
                ? userProfile?.activeCommunity?.nonProfit?.id
                : selectedNonProfit?.id
            }
            orgType={userProfile?.activeCommunity?.orgType}
          />
        )}
      </div> */}

      {(isNekiAdmin || !isNonProfitAdmin) && (
        <Controller
          control={control}
          name="nonProfit"
          render={({ field: { onChange, value } }) => (
            <NonProfitSearchBox
              isNekiAdmin={isNekiAdmin}
              campaignType={type}
              value={value}
              onChange={(val) => {
                onChange(val);
                clearFormOnNonProfitChange(val);
              }}
              error={errors.nonProfit?.message}
            />
          )}
        />
      )}

      <Controller
        control={control}
        name="project"
        render={({ field: { onChange, value } }) => (
          <ProjectSelector
            value={value}
            onChange={(val) => {
              onChange(val);
              handleProjectChange(val);
            }}
            projects={projects}
            error={errors.project?.message}
            required={type === CampaignType.CollectGoods}
          />
        )}
      />

      <div className="flex flex-col gap-6">
        <Controller
          control={control}
          name="title"
          render={({ field }) => (
            <Input
              {...field}
              label="Campaign Name"
              placeholder="Campaign Name"
              error={errors.title?.message || ''}
              required
            />
          )}
        />

        <div className="align-center flex flex-col flex-nowrap justify-between gap-6 sm:flex-row sm:gap-2">
          <div className="w-full grow">
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <TextArea
                  {...field}
                  label="Description"
                  placeholder="Description"
                  rows={3}
                  className="w-full"
                  error={errors.description?.message}
                  required
                />
              )}
            />
          </div>
          {type === CampaignType.CollectGoods && (
            <div className="w-full grow">
              <Controller
                control={control}
                name="address"
                render={({ field }) => (
                  <TextArea
                    {...field}
                    label="Address"
                    placeholder="The address at which the campaign will be held"
                    rows={3}
                    className="w-full"
                    error={errors.address?.message}
                    required
                  />
                )}
              />
            </div>
          )}
        </div>

        {type === CampaignType.FundRaise && (
          <Controller
            control={control}
            name="goalAmount"
            render={({ field: { onChange, ...rest } }) => (
              <Input
                {...rest}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (!isNaN(val)) {
                    onChange(e.target.value);
                  } else {
                    onChange('');
                  }
                }}
                label="Goal"
                placeholder="The money that you are raising for this campaign"
                error={errors.goalAmount?.message || ''}
                adornment={
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className="font-medium">
                    {userProfile.location.currencySymbol}
                  </Typography>
                }
                required
              />
            )}
          />
        )}
        <div className="flex w-full flex-col items-start gap-4 sm:flex-row">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Controller
              control={control}
              name="startDate"
              render={({ field: { value, onChange } }) => (
                <MobileDatePicker
                  inputFormat="MMM dd, yyyy"
                  value={value}
                  onChange={onChange}
                  renderInput={({ inputProps, onClick }) => (
                    <Input
                      {...inputProps}
                      onClick={onClick}
                      value={value?.toDateString()}
                      label="Start Date"
                      required
                      tooltip="Launch date for when donations start rolling in"
                      error={errors.startDate?.message}
                    />
                  )}
                />
              )}
            />

            <Controller
              control={control}
              name="endDate"
              render={({ field: { value, onChange } }) => (
                <MobileDatePicker
                  inputFormat="MMM dd, yyyy"
                  value={value}
                  onChange={onChange}
                  renderInput={({ inputProps, onClick }) => (
                    <Input
                      {...inputProps}
                      onClick={onClick}
                      value={value?.toDateString()}
                      label="End Date"
                      required
                      tooltip={`Campaign closes after this date"`}
                      error={errors.endDate?.message}
                    />
                  )}
                />
              )}
            />
          </LocalizationProvider>
        </div>

        {isEmpty(images) && !isEmpty(projectImages) && (
          <div>
            <ImageCarousel size="xs" images={projectImages} disabled />
          </div>
        )}
        {isEmpty(videos) && !isEmpty(projectVideos) && (
          <div>
            <ImageCarousel
              type="video"
              size="xs"
              images={projectVideos}
              disabled
            />
          </div>
        )}

        {!isEmpty(selectedProject) && (
          <div className="flex flex-col gap-4">
            <div className="border-b">
              <Typography variant="body1" component="span">
                Giving Opportunities for {selectedProject.name}
              </Typography>
            </div>
            {selectedProject.givingLevels.map((givingLevel) => (
              <div className="flex flex-col gap-2" key={givingLevel._id}>
                <Typography variant="body1">
                  <b>Name: </b>
                  {givingLevel.title}
                </Typography>
                <Typography variant="body1">
                  <b>Quantity: </b> {quantityPrefix}
                  {givingLevel.quantity}
                </Typography>
                <Typography variant="body1">
                  <b>Description: </b> {givingLevel.description}
                </Typography>
                <div className="border-b" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateCampaignForm;
